/**
 * Focus Activity Simulator (Refactored)
 * State machine for focus activities with extracted components
 *
 * Refactored from 559 LOC to component-based architecture
 */

import React, { useState, useEffect } from 'react';
import { getNeuroMessage, FocusActivityType } from './focus-activities/NeuroMessages';
import { IntroModal } from './focus-activities/IntroModal';
import { CompleteModal } from './focus-activities/CompleteModal';
import { ActivityHeader } from './focus-activities/ActivityHeader';
import { VisualTrackingActivity } from './focus-activities/VisualTrackingActivity';
import { PatternMemoryActivity } from './focus-activities/PatternMemoryActivity';
import { CalmBreathingActivity } from './focus-activities/CalmBreathingActivity';
import { DelayGratificationActivity } from './focus-activities/DelayGratificationActivity';
import { AudioSequencingActivity } from './focus-activities/AudioSequencingActivity';
import { StillnessFocusActivity } from './focus-activities/StillnessFocusActivity';

interface FocusActivity {
  id: string;
  title: string;
  description: string;
  type: FocusActivityType;
  duration: number;
  difficulty: 'easy' | 'medium' | 'hard';
  points_reward: number;
}

interface FocusActivitySimulatorProps {
  activity: FocusActivity;
  onComplete: (score: number, timeSpent: number) => void;
  onClose: () => void;
}

export function FocusActivitySimulator({ activity, onComplete, onClose }: FocusActivitySimulatorProps) {
  // Phase state machine
  const [phase, setPhase] = useState<'intro' | 'activity' | 'complete'>('intro');
  const [timeLeft, setTimeLeft] = useState(activity.duration);
  const [startTime] = useState(Date.now());
  const [score, setScore] = useState(0);

  // Activity interaction tracking
  const [userInteractions, setUserInteractions] = useState<any[]>([]);

  // Timer logic
  useEffect(() => {
    if (phase === 'activity' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (phase === 'activity' && timeLeft === 0) {
      completeActivity();
    }
  }, [phase, timeLeft]);

  const startActivity = () => {
    setPhase('activity');
  };

  const completeActivity = () => {
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    const finalScore = calculateScore();
    setScore(finalScore);
    setPhase('complete');

    setTimeout(() => {
      onComplete(finalScore, timeSpent);
    }, 2000);
  };

  const calculateScore = () => {
    const completionBonus = timeLeft > 0 ? 20 : 10;
    const interactionBonus = Math.min(userInteractions.length * 5, 30);
    const difficultyMultiplier = activity.difficulty === 'hard' ? 1.5 : activity.difficulty === 'medium' ? 1.2 : 1;

    return Math.round((completionBonus + interactionBonus) * difficultyMultiplier);
  };

  const handleInteraction = (data: any) => {
    setUserInteractions(prev => [...prev, data]);
  };

  const messages = getNeuroMessage(activity.type);

  const renderActivityContent = () => {
    switch (activity.type) {
      case 'visual_tracking':
        return <VisualTrackingActivity timeLeft={timeLeft} onInteraction={handleInteraction} />;
      case 'pattern_memory':
        return <PatternMemoryActivity timeLeft={timeLeft} onInteraction={handleInteraction} />;
      case 'calm_breathing':
        return <CalmBreathingActivity timeLeft={timeLeft} onInteraction={handleInteraction} />;
      case 'delay_gratification':
        return <DelayGratificationActivity timeLeft={timeLeft} onInteraction={handleInteraction} />;
      case 'audio_sequencing':
        return <AudioSequencingActivity timeLeft={timeLeft} onInteraction={handleInteraction} />;
      case 'stillness_focus':
        return <StillnessFocusActivity timeLeft={timeLeft} onInteraction={handleInteraction} />;
      default:
        return <div className="text-center text-gray-600">Activity content coming soon!</div>;
    }
  };

  // Render phase-specific UI
  if (phase === 'intro') {
    return (
      <IntroModal
        activity={activity}
        messages={messages}
        onClose={onClose}
        onStart={startActivity}
      />
    );
  }

  if (phase === 'complete') {
    return <CompleteModal messages={messages} score={score} />;
  }

  // Activity phase
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 overflow-hidden">
        <ActivityHeader
          activityTitle={activity.title}
          timeLeft={timeLeft}
          onClose={onClose}
        />

        {/* Activity Content */}
        <div className="p-8 min-h-[400px] flex items-center justify-center">
          {renderActivityContent()}
        </div>
      </div>
    </div>
  );
}
